import classNames from "classnames";
import Flickity from "flickity-imagesloaded";
import React from "react";
import { useMountEffect } from "tsi-common-react/src/hooks/useMountEffect";
import { check } from "tsi-common-react/src/models/utils";

import { ReviewsBlock as ReviewsBlockCodec } from "../models";

import styles from "./ReviewsBlock.module.scss";

const THEME_STARS_BG = "theme--stars-and-bg";

const isStarsTheme = (theme?: string): boolean => {
    return theme === THEME_STARS_BG;
};

const renderAuthor = (
    author: string,
    location: string,
    theme?: string,
): React.ReactNode => {
    if (isStarsTheme(theme)) {
        // TODO(i18n): Use t with interpolation to keep sentence together: {t`– ${author} in ${location}`}
        return (
            <>
                – <em>{author}</em> in {location}
            </>
        );
    }
    // TODO(i18n): Use t with interpolation to keep sentence together: {t`${author}, ${location}`}
    return (
        <>
            <strong>{author}</strong>, {location}
        </>
    );
};

export const ReviewsBlock = (props: {
    reviewsBlockJSON: string;
    theme?: string;
}): React.ReactNode => {
    const carouselRef = React.useRef<HTMLDivElement | null>(null);
    const flickityRef = React.useRef<Flickity | null>(null);

    const reviewsBlock = check(
        ReviewsBlockCodec.decode(JSON.parse(props.reviewsBlockJSON)),
    );

    useMountEffect(() => {
        if (!carouselRef.current) {
            return;
        }
        if (
            !isStarsTheme(props.theme) &&
            carouselRef.current.childNodes.length > 1
        ) {
            if (flickityRef.current) {
                flickityRef.current.destroy();
            }
            flickityRef.current = new Flickity(carouselRef.current, {
                watchCSS: true,
                wrapAround: true,
                prevNextButtons: false,
                pageDots: true,
            });
        }

        return () => {
            if (flickityRef.current) {
                flickityRef.current.destroy();
                flickityRef.current = null;
            }
        };
    });

    const columnClasses = classNames({
        [styles.column]: true,
        [styles.columnThemeStars]: isStarsTheme(props.theme),
    });
    const contentClasses = classNames({
        [styles.content]: true,
        [styles.contentThemeStars]: isStarsTheme(props.theme),
    });
    const authorClasses = classNames({
        [styles.author]: true,
        [styles.authorThemeStars]: isStarsTheme(props.theme),
    });

    return (
        <div className={styles.columns} ref={carouselRef}>
            {reviewsBlock.reviews.map((review, i) => (
                <div className={columnClasses} key={i}>
                    <div className={contentClasses}>{review.content}</div>
                    <div className={authorClasses}>
                        {renderAuthor(
                            review.author,
                            review.location,
                            props.theme,
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};
